/* eslint-disable react/require-default-props */
import { Bill } from '@enview/interface/types/bills/Bill';
import { ReactElement, useMemo } from 'react';
import BillBulkActionsDropdown from '../DropDowns/BillBulkActionsDropdown';
import BillViewDropdown from '../DropDowns/BillViewDropdown';
import MultipleSelectDropDown, {
  OptionType,
} from '../DropDowns/MultipleSelectDropDown';
import BillActionFilterDropdown from '../../LegislativeTracking/SearchFilters/BillActionFilterDropdown';
import { REQUIRED_COLUMNS, SearchActionFilter } from '@enview/interface/types/Bill';
import BillStatusFilterDropdown from '../../LegislativeTracking/SearchFilters/BillStatusFilterDropdown';
import PartySponsorFilterDropdown from '../../LegislativeTracking/SearchFilters/PartySponsorFilterDropdown';
import { getStatusOptionsForJurisdictions } from '../../../helpers/BillSearchHelper';
import { DecodedValueMap, QueryParamConfigMap, SetQuery } from 'use-query-params';
import TagNameFilter from '../Search/MultiSelectFilter';
import ColumnSelectDropdown from '../DropDowns/ColumnSelectDropdown';
import { CustomFieldsAPI } from '../../../api';
import { useSelector } from 'react-redux';
import { getOrgForTeamMode } from '../../../dux';
import { buildColumnOptions } from '../../../utils';
import { skipToken } from '@reduxjs/toolkit/query';

export type BillTableFilterProperties = {
  filterLabel?: string;
  selectedFilters?: string[];
  filterOptions?: OptionType<string>[];
  setSelectedFilters?: (selection: string[]) => void;
  tagFilterOptions?: OptionType<string>[];
  selectedTags?: string[];
  setSelectedTags?: (tags: string[]) => void;
  actionFilter?: SearchActionFilter | undefined;
  setActionFilter?: (arg: SearchActionFilter | undefined) => void;
  statusFilters?: string[];
  setStatusFilters?: (filters: string[]) => void;
  partySponsorFilters?: string[];
  setPartySponsorFilters?: (filters: string[]) => void;
  selectedTagFilterOption?: string;
  setSelectedTagFilterOption?: (option: string) => void;
};

type BillTableHeaderProps = {
  results: { data: Bill[]; count: number };
  selectedRows: Bill[];
  showFilterProperties: boolean;
  filterProperties?: BillTableFilterProperties;
  query: DecodedValueMap<QueryParamConfigMap>;
  setQuery: SetQuery<QueryParamConfigMap>;
  selectedColumns: string[];
  setSelectedColumns: (a: any) => void;
};

const BillTableHeader = (props: BillTableHeaderProps): ReactElement => {
  const {
    showFilterProperties,
    filterProperties,
    results,
    selectedRows,
    query,
    setQuery,
    selectedColumns,
    setSelectedColumns,
  } = props;
  const {
    filterLabel = 'Jurisdiction',
    selectedFilters = [],
    filterOptions = [],
    tagFilterOptions,
  } = filterProperties || {};

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const { data: customFields = [] } =
    CustomFieldsAPI.endpoints.getOrganizationCustomFields.useQuery(
      orgId ? orgId : skipToken,
    );

  const columnOptions = useMemo(() => buildColumnOptions(customFields), [customFields]);

  const handleColumnChange = (values: string[]) => {
    const withRequired = Array.from(new Set([...values, ...REQUIRED_COLUMNS]));
    setSelectedColumns(withRequired);
  };

  const statusOptions = getStatusOptionsForJurisdictions(selectedFilters);

  const selectedStatuses = query.status || [];
  const selectedSessions =
    filterLabel === 'Jurisdiction' ? query?.jurisdictions : query?.sessions || [];
  const selectedSponsorFilters = query.sponsors || [];
  const currentActionFilter =
    query.searchActionType && query.searchActionFromDate && query.searchActionToDate
      ? {
          type: query.searchActionType,
          fromDate: query.searchActionFromDate,
          toDate: query.searchActionToDate,
        }
      : undefined;

  const getQueryUpdate = (selectedValues: string[]) => {
    if (filterLabel === 'Jurisdiction')
      return {
        jurisdictions: selectedValues,
      };
    return { sessions: selectedValues };
  };
  return (
    <div className="row bill-table-header">
      {showFilterProperties && (
        <div className="jurisdiction-filter">
          <div className="filter-group">
            <div className="total-count">
              <span>
                {results?.count ?? 0} bill{results && results?.count == 1 ? '' : 's'}
              </span>
            </div>
            <div className="filter jurisdictions">
              <MultipleSelectDropDown
                currentSelectedValues={selectedSessions}
                onChange={(selectedFilterValues) => {
                  setQuery(getQueryUpdate(selectedFilterValues), 'replaceIn');
                }}
                options={filterOptions}
                title={`Filter by ${filterLabel}`}
              />
            </div>
            {tagFilterOptions && (
              <TagNameFilter
                className="tagnames"
                options={tagFilterOptions}
                selectedFilterOption={query?.tagFilterMode}
                selectedValues={query?.tags ?? []}
                setSelectedFilterOption={(modeSelection) => {
                  setQuery({ tagFilterMode: modeSelection }, 'replaceIn');
                }}
                setSelectedValues={(newSelectedTags) => {
                  setQuery({ tags: newSelectedTags }, 'replaceIn');
                }}
                title="Tag Names"
              />
            )}
            <BillActionFilterDropdown
              billAction={currentActionFilter}
              setBillAction={(arg: SearchActionFilter | undefined) => {
                if (arg) {
                  setQuery(
                    {
                      searchActionType: arg.type,
                      searchActionFromDate: arg.fromDate,
                      searchActionToDate: arg.toDate,
                    },
                    'replaceIn',
                  );
                } else {
                  setQuery({
                    searchActionType: undefined,
                    searchActionFromDate: undefined,
                    searchActionToDate: undefined,
                  });
                }
              }}
              setSearchChanged={(_b) => {}}
            />
            <BillStatusFilterDropdown
              handleStatusFiltersChange={(filters) => {
                setQuery({ status: filters }, 'replaceIn');
              }}
              status={selectedStatuses}
              statusOptions={statusOptions}
            />
            <PartySponsorFilterDropdown
              handleSponsorFiltersChange={(filters) => {
                setQuery({ sponsors: filters }, 'replaceIn');
              }}
              sponsorFilters={selectedSponsorFilters}
            />
          </div>
          <div className="actions-view-group mt-3">
            <BillBulkActionsDropdown bills={selectedRows} />
            <div className="ml-auto">
              {orgId && (
                <ColumnSelectDropdown
                  columnOptions={columnOptions}
                  selectedColumns={selectedColumns}
                  setSelectedColumns={handleColumnChange}
                />
              )}
            </div>
            <div className="view-dropdown dropdown-container">
              <BillViewDropdown />
            </div>
          </div>
        </div>
      )}

      {!showFilterProperties && (
        <>
          <div className="filtered-group result-total">
            <div className="result-dropdown-container">
              <div className="total-count mb-2">
                <span>
                  {results?.count ?? 0} bill{results && results?.count == 1 ? '' : 's'}
                </span>
              </div>
              <BillBulkActionsDropdown bills={selectedRows} />
            </div>
          </div>
          <div
            className="view-dropdown dropdown-container"
            style={{ paddingLeft: '74px' }}
          >
            <div className="mr-2">
              <ColumnSelectDropdown
                columnOptions={columnOptions}
                selectedColumns={selectedColumns}
                setSelectedColumns={handleColumnChange}
              />
            </div>
            <BillViewDropdown />
          </div>
        </>
      )}
    </div>
  );
};

export default BillTableHeader;
